import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiControlledTextField,
  MiIcon,
  MiIconButton,
  MiPlaceholderArea,
} from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiRefresh } from "@mdi/js";
import { Box, Tooltip, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  IDrawsDistributionFormData,
  IDropDraw,
} from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import { customerDrawsDistributionTabSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerDrawsDistributionTabViewModel } from "./useCustomerDrawsDistributionTabViewModel";

export function CustomerDrawsDistributionTab() {
  const { classes, theme } = useStyles();
  const { accountDetails, setSaveHandler, setConfirmModalConfig } =
    useCustomerPricingDetailsContext();

  const {
    defaultDraws,
    categories,
    dropDraws,
    getDefaultDraws,
    getCategories,
    getDropDraws,
    updateCategoryDraws,
    updateDropDraws,
    isLoading,
  } = useCustomerDrawsDistributionTabViewModel();

  const form = useForm<IDrawsDistributionFormData>({
    mode: "onChange",
    defaultValues: {
      dropDistribution: { draws: [] },
      categoryDistributions: [],
    },
    resolver: zodResolver(customerDrawsDistributionTabSchema),
  });

  const { control, getValues, setValue, reset } = form;

  useTabFormRegistration(form, isLoading);

  useEffect(() => {
    if (!dropDraws.length) {
      setConfirmModalConfig(null);
      return;
    }

    setConfirmModalConfig({
      description: `You are about to update the draw distribution for ${
        accountDetails?.name ?? "this account"
      }. Are you sure you would like to continue?`,
    });
  }, [accountDetails, dropDraws]);

  useEffect(() => {
    setSaveHandler(async () => {
      const accountId = accountDetails?.id ?? "";
      const { dirtyFields } = form.formState;

      const dropRequest = dirtyFields.dropDistribution
        ? updateDropDraws(accountId, getValues("dropDistribution.draws"))
        : null;

      const categoryRequest = dirtyFields.categoryDistributions
        ? updateCategoryDraws(
            accountId,
            getValues("categoryDistributions").map((category) => ({
              categoryId: category.categoryId,
              multiplier: null,
              markup: null,
              draws: category.draws.map((draw) => ({
                categoryId: draw.categoryId,
                accountDrawId: draw.accountDrawId,
                percentage: draw.percentage,
              })),
              modifiedOn: category.modifiedOn ?? "",
            }))
          )
        : null;

      await Promise.all([dropRequest, categoryRequest]);
      return dropRequest ?? categoryRequest ?? undefined;
    });
  }, [accountDetails]);

  useEffect(() => {
    if (accountDetails) {
      getDefaultDraws(accountDetails.id);
      getCategories(accountDetails.id);
      getDropDraws(accountDetails.id);
    }
  }, [accountDetails]);

  // initialize form when draws, categories, and drop draws are loaded
  useEffect(() => {
    if (defaultDraws.length && categories.length && accountDetails) {
      // Map each accountDraw to its saved drop draw percentage
      const formDropDraws: IDropDraw[] = defaultDraws.map((d) => {
        const dropDraw = dropDraws.find((dd) => dd.accountDrawId === d.id);
        return {
          accountId: accountDetails.id,
          accountDrawId: d.id ?? "",
          percentage: dropDraw?.percentage ?? 0,
        };
      });
      const dropTotal = calculateTotal(formDropDraws.map((d) => d.percentage));

      reset({
        dropDistribution: { draws: formDropDraws, dropTotal },
        categoryDistributions: categories,
      });
    }
  }, [defaultDraws, categories, dropDraws, accountDetails, reset]);

  const calculateTotal = (percentages: number[]) => {
    return Number.parseFloat(
      percentages
        .reduce((acc, p) => acc + (Number.isNaN(p) ? 0 : p), 0)
        .toFixed(2)
    );
  };

  const handleResetAllDropPercentages = () => {
    defaultDraws.forEach((draw, index) => {
      setValue(`dropDistribution.draws.${index}.percentage`, draw.percentage, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });
    setValue(
      "dropDistribution.dropTotal",
      calculateTotal(defaultDraws.map((d) => d.percentage)),
      { shouldValidate: true }
    );
  };

  const handleResetAllCategoryPercentages = (categoryIndex: number) => {
    defaultDraws.forEach((draw, drawIndex) => {
      setValue(
        `categoryDistributions.${categoryIndex}.draws.${drawIndex}.percentage`,
        draw.percentage,
        { shouldValidate: true, shouldDirty: true }
      );
    });
    setValue(
      `categoryDistributions.${categoryIndex}.drawTotal`,
      calculateTotal(defaultDraws.map((d) => d.percentage)),
      { shouldValidate: true }
    );
  };

  const isDropRowOverridden = () => {
    return defaultDraws.some((draw, index) => {
      const currentValue = getValues(
        `dropDistribution.draws.${index}.percentage`
      );
      return (
        !isLoading &&
        !Number.isNaN(currentValue) &&
        currentValue !== draw.percentage
      );
    });
  };

  const isCategoryRowOverridden = (categoryIndex: number) => {
    return defaultDraws.some((draw, drawIndex) => {
      const currentValue = getValues(
        `categoryDistributions.${categoryIndex}.draws.${drawIndex}.percentage`
      );
      return !Number.isNaN(currentValue) && currentValue !== draw.percentage;
    });
  };

  const handleDropPercentageChange = (
    drawIndex: number,
    floatValue: number | undefined
  ) => {
    const value = floatValue ?? Number.NaN;
    setValue(`dropDistribution.draws.${drawIndex}.percentage`, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
    const dropDraws = getValues("dropDistribution.draws");
    setValue(
      "dropDistribution.dropTotal",
      calculateTotal(dropDraws.map((d) => d.percentage)),
      { shouldValidate: true }
    );
  };

  const handleCategoryPercentageChange = (
    categoryIndex: number,
    drawIndex: number,
    floatValue: number | undefined
  ) => {
    const value = floatValue ?? Number.NaN;
    setValue(
      `categoryDistributions.${categoryIndex}.draws.${drawIndex}.percentage`,
      value,
      { shouldValidate: true, shouldDirty: true }
    );
    const categoryDraws = getValues(
      `categoryDistributions.${categoryIndex}.draws`
    );
    setValue(
      `categoryDistributions.${categoryIndex}.drawTotal`,
      calculateTotal(categoryDraws.map((d) => d.percentage)),
      { shouldValidate: true }
    );
  };

  const hasDraws = defaultDraws.length > 0;

  const renderEmptyPlaceholder = () => (
    <MiPlaceholderArea
      text="No Draw Created"
      helperText="Tip: Go to Draws Setup step to create a draw."
      iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
      containerStyle={{
        backgroundColor: theme.palette.primary.light,
        height: "100%",
        minHeight: 200,
      }}
    />
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <ConfigAccordionHeader title="Draw Distribution by Drop">
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            color={theme.palette.text.secondary}
          >
            Draw distribution applies to all Drops.
          </Typography>
          {hasDraws ? (
            <Box className={classes.tableContainer}>
              <Box className={classes.tableRow}>
                <Box className={classes.rowLabelContainer}>
                  <Typography
                    className={classes.rowLabel}
                    variant={TYPOGRAPHY_VARIANTS.BODY1}
                  >
                    Drops
                  </Typography>
                  {isDropRowOverridden() && (
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.BODY2}
                      color={theme.palette.warning.main}
                    >
                      Manually overridden.
                    </Typography>
                  )}
                </Box>
                <Box className={classes.distributionRow}>
                  <Box className={classes.resetButtonContainer}>
                    {isDropRowOverridden() && (
                      <Tooltip title="Reset to default distribution" arrow>
                        <MiIconButton
                          size="small"
                          onClick={handleResetAllDropPercentages}
                          className={classes.resetButton}
                        >
                          <MiIcon path={mdiRefresh} size={0.8} />
                        </MiIconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {defaultDraws.map((draw, drawIndex) => {
                    const currentDropValue = getValues(
                      `dropDistribution.draws.${drawIndex}.percentage`
                    );
                    const isDropOverridden =
                      !isLoading &&
                      !Number.isNaN(currentDropValue) &&
                      currentDropValue !== draw.percentage;

                    return (
                      <Box
                        key={draw.id}
                        className={classes.inputCell}
                        data-overridden={isDropOverridden ? "true" : "false"}
                      >
                        <NumericFormat
                          customInput={MiControlledTextField}
                          thousandSeparator
                          decimalScale={2}
                          fixedDecimalScale
                          allowNegative={false}
                          allowLeadingZeros={false}
                          suffix="%"
                          name={`dropDistribution.draws.${drawIndex}.percentage`}
                          disabled={isLoading}
                          formControl={control}
                          label={`${draw.name} (%)`}
                          value={
                            Number.isNaN(currentDropValue)
                              ? undefined
                              : currentDropValue
                          }
                          onValueChange={({ floatValue }) => {
                            handleDropPercentageChange(drawIndex, floatValue);
                          }}
                        />
                      </Box>
                    );
                  })}
                  <Box className={classes.totalCell}>
                    <NumericFormat
                      customInput={MiControlledTextField}
                      formControl={control}
                      name="dropDistribution.dropTotal"
                      label="Total Distribution (%)"
                      value={getValues("dropDistribution.dropTotal") ?? 0}
                      thousandSeparator
                      decimalScale={2}
                      allowNegative={false}
                      suffix="%"
                      readOnly
                      disabled
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            renderEmptyPlaceholder()
          )}
        </Box>
      </ConfigAccordionHeader>

      <ConfigAccordionHeader title="Draw Distribution by Category">
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            color={theme.palette.text.secondary}
          >
            Draw distribution can be customized per Category.
          </Typography>
          {hasDraws && categories.length > 0 ? (
            <Box className={classes.tableContainer}>
              {categories.map((category, categoryIndex) => (
                <Box key={category.categoryId} className={classes.tableRow}>
                  <Box className={classes.rowLabelContainer}>
                    <Typography
                      className={classes.rowLabel}
                      variant={TYPOGRAPHY_VARIANTS.BODY1}
                    >
                      {category.categoryName}
                    </Typography>
                    {isCategoryRowOverridden(categoryIndex) && (
                      <Typography
                        variant={TYPOGRAPHY_VARIANTS.BODY2}
                        color={theme.palette.warning.main}
                      >
                        Manually overridden.
                      </Typography>
                    )}
                  </Box>
                  <Box className={classes.distributionRow}>
                    <Box className={classes.resetButtonContainer}>
                      {isCategoryRowOverridden(categoryIndex) && (
                        <Tooltip title="Reset to default distribution" arrow>
                          <MiIconButton
                            size="small"
                            onClick={() =>
                              handleResetAllCategoryPercentages(categoryIndex)
                            }
                            className={classes.resetButton}
                          >
                            <MiIcon path={mdiRefresh} size={0.8} />
                          </MiIconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {defaultDraws.map((draw, drawIndex) => {
                      const currentValue = getValues(
                        `categoryDistributions.${categoryIndex}.draws.${drawIndex}.percentage`
                      );
                      const isOverridden =
                        !Number.isNaN(currentValue) &&
                        currentValue !== draw.percentage;

                      return (
                        <Box
                          key={draw.id}
                          className={classes.inputCell}
                          data-overridden={isOverridden ? "true" : "false"}
                        >
                          <NumericFormat
                            customInput={MiControlledTextField}
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            allowLeadingZeros={false}
                            suffix="%"
                            name={`categoryDistributions.${categoryIndex}.draws.${drawIndex}.percentage`}
                            disabled={isLoading}
                            formControl={control}
                            label={`${draw.name} (%)`}
                            value={
                              Number.isNaN(currentValue)
                                ? undefined
                                : currentValue
                            }
                            onValueChange={({ floatValue }) => {
                              handleCategoryPercentageChange(
                                categoryIndex,
                                drawIndex,
                                floatValue
                              );
                            }}
                          />
                        </Box>
                      );
                    })}
                    <Box className={classes.totalCell}>
                      <NumericFormat
                        customInput={MiControlledTextField}
                        formControl={control}
                        name={`categoryDistributions.${categoryIndex}.drawTotal`}
                        label="Total Distribution (%)"
                        value={
                          getValues(
                            `categoryDistributions.${categoryIndex}.drawTotal`
                          ) ?? 0
                        }
                        thousandSeparator
                        decimalScale={2}
                        allowNegative={false}
                        suffix="%"
                        readOnly
                        disabled
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            renderEmptyPlaceholder()
          )}
        </Box>
      </ConfigAccordionHeader>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  tableRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(4, 2, 2, 3),
    backgroundColor: theme.palette.grey.mediumLight,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    transition: "height 0.3s ease",
  },
  rowLabelContainer: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 1,
    width: 240,
    minWidth: 120,
    paddingBottom: theme.spacing(2),
  },
  rowLabel: {
    fontWeight: theme.fontWeight.semiBold,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  resetButtonContainer: {
    width: 32,
    minWidth: 32,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  resetButton: {
    flexShrink: 0,
  },
  distributionRow: {
    display: "flex",
    flex: 1,
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    minWidth: 0,
  },
  inputCell: {
    flex: 1,
    maxWidth: 280,
    minWidth: 10,
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.common.white,
    },
    "&[data-overridden='true'] .MuiOutlinedInput-root:not(.Mui-error)": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: `${theme.palette.warning.main}`,
      },
    },
    "&[data-overridden='true'] .MuiInputLabel-root.Mui-focused:not(.Mui-error)":
      {
        color: theme.palette.warning.main,
      },
  },
  totalCell: {
    flex: 1,
    minWidth: 130,
    maxWidth: 180,
    "& .MuiFormHelperText-root.Mui-disabled": {
      color: `${theme.palette.error.main} !important`,
    },
  },
}));
